// @flow

import axios from "axios";

export type Cache<V> = {|
  get: string => Promise<V>,
  size: () => number
|};

/**
 * A simple cache of network resources to help with reducing the number of
 * external requests that need to be made for resources.
 *
 * The targetSize provided allows the cache to not grow unbounded. Size
 * reduction is handled in a very naive approach of evicting the oldest records.
 * Capacity allows further customization to provide a maximum size that the
 * cache should be allowed to grow to. If a capacity is not supplied, then the
 * default capacity is 150% of the targetSize.
 *
 * Note: The size of the cache *may* grow larger than the capacity at any given
 * time, but the cache will then attempt to reduce its size when possible.
 *
 * @param targetSize
 * @param capacity
 * @param httpClient
 * @returns {{get: get}}
 */
export default function makeCache<K, V>(
  targetSize: number,
  capacity?: number,
  httpClient?: Object
): Cache<V> {
  let innerClient = httpClient || axios;
  const MAX = capacity != null ? capacity : targetSize * 1.5;

  let asyncRunner = typeof requestIdleCallback === 'function' ?
    requestIdleCallback : setTimeout;

  // The response cache, capped by the target size.
  let cache: Map<string, V> = new Map();

  // A cache of currently pending requests. This helps to de-duplicate work. If
  // the client is already waiting on a response for a given url, then it can
  // use that same Promise for another caller.
  let pendingCache: Map<string, Promise<V>> = new Map();

  // Cache of failed requests, this should possibly just also use a static
  // failure object as its value instead of holding on to responses. This is
  // currently an uncapped collection.
  let errorCache: Map<string, any> = new Map();

  // Flag to ensure that only a single cache purge is scheduled at a time.
  let clearScheduled = false;

  // This is a naive way of adding a maximum capacity to our cache. To improve
  // this we would use some kind of age + use frequency to determine what keys
  // to purge. Instead we just purge the oldest records.
  function clearOldEntries() {
    let keys = cache.keys();
    let excessCount = cache.size - targetSize;
    let keysToRemove = [];

    for (let i = 0; i < excessCount; i++) {
      let v = keys.next().value;

      if (v != null) {
        keysToRemove.push(v);
      }
    }

    keysToRemove.forEach(key => {
      cache.delete(key);
    });

    clearScheduled = false;
  }

  return {
    get: function(url: string): Promise<V> {
      let cacheCheck = cache.get(url);

      if (cacheCheck != null) {
        return Promise.resolve(cacheCheck);
      }

      // Check the 404 cache
      let notFoundCheck = errorCache.get(url);

      if (notFoundCheck) {
        return Promise.reject(notFoundCheck);
      }

      let pendingCheck = pendingCache.get(url);

      if (pendingCheck) {
        return pendingCheck;
      }

      let p = innerClient.get(url)
        .then(resp => {
          cache.set(url, resp.data);
          pendingCache.delete(url);

          if (cache.size > MAX && !clearScheduled) {
            asyncRunner(clearOldEntries);
            clearScheduled = true;
          }

          return resp.data;
        })
        .catch(resp => {
          errorCache.set(url, resp);
          pendingCache.delete(url);
          throw resp;
        });

      pendingCache.set(url, p);

      return p;
    },
    size: function() {
      return cache.size;
    }
  };
}